import { msg } from '@gonfalon/messages/msg';
import { Group, Input, TextField } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { isTabbable } from '@react-aria/utils';

import { CommandPalettePendingIndicator } from './command-palette-pending-indicator';

import styles from './command-palette-search-field.module.css';

export interface CommandPaletteSearchFieldProps {
  onKeyDown: (e: React.KeyboardEvent) => void;
  /**
   * Capture-phase key handler. Used by the autocomplete shell to
   * activate the visibly highlighted row on Enter before RAC's own
   * bubble handler runs (see CommandPaletteAutocomplete).
   */
  onKeyDownCapture?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Placeholder text for the input. Pre-computed by the caller; this
   * component doesn't reach into the rootCommand or otherwise derive
   * its own placeholder.
   */
  placeholder: string;
  /**
   * When present, render an entered-root pill (`<prefix><key>`)
   * ahead of the input. Both fields are pre-computed by the caller:
   * the displayed key is the snap (or id fallback), the ariaLabel
   * is the user-visible "Inside <root label>" string.
   */
  pill?: { key: string; ariaLabel: string };
}

/**
 * Search field for the command palette.
 *
 * A thin presentational component: leading icon, optional snap pill,
 * the input itself, and the pending indicator. The pending indicator
 * is its own component so registry churn (every consumer publishing
 * commands) doesn't re-render the input subtree.
 */
export function CommandPaletteSearchField({
  onKeyDown,
  onKeyDownCapture,
  placeholder,
  pill,
}: CommandPaletteSearchFieldProps) {
  // The search input is the canonical focus target in the palette.
  // RAC's <Modal> wraps its children in FocusScope contain, which
  // pulls focus back into the scope when it would otherwise escape
  // (e.g., when the user clicks on a non-focusable in-dialog element
  // like a section header or the dialog's padding). FocusScope's
  // recovery target is the dialog wrapper itself (tabIndex=-1), not
  // the input -- so focus drifts off the input and onto a wrapper
  // the user can't type into.
  //
  // Snap focus back to the input whenever it's about to land
  // somewhere that isn't user-meaningful. We use isTabbable from
  // @react-aria/focus as the predicate: real focus targets (input,
  // button, links, anything with tabIndex >= 0) pass through so
  // future Tab gestures keep working; tabIndex=-1 wrappers and
  // null (no focus target) trigger snapback.
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.relatedTarget === null || !isTabbable(e.relatedTarget)) {
      e.target.focus();
    }
  };

  return (
    <TextField aria-label={msg('commandPalette.search.label')}>
      <Group>
        <Icon name="search" size="small" />
        {pill !== undefined && (
          <span className={styles.snapPill} aria-label={pill.ariaLabel}>
            {pill.key}
          </span>
        )}
        <Input
          autoFocus
          placeholder={placeholder}
          onKeyDown={onKeyDown}
          onKeyDownCapture={onKeyDownCapture}
          onBlur={handleBlur}
        />
        <CommandPalettePendingIndicator />
      </Group>
    </TextField>
  );
}
